// Adapts BaseDashboard/wwwroot/js/configurator-viewer.js to React.
// The JS module expects a Blazor-shaped DotNetObjectReference: an object with
// .invokeMethodAsync(method, ...args). We hand it a tiny shim that routes to React handlers.

export interface BridgeHandlers {
  OnViewerProgress?: (progress: number) => void;
  OnViewerLoaded?: () => void;
  OnViewerError?: (error: string) => void;
  /**
   * Catch-all for messages from Unity, routed via the WebBridge once registered.
   * messageType examples: "event:ProductSelected", "RequestTokenRefresh", etc.
   */
  HandleUnityMessage?: (messageType: string, data: string) => void;
}

export interface BridgeRef {
  invokeMethodAsync: (method: string, ...args: unknown[]) => Promise<unknown>;
}

export function makeBridgeRef(handlers: BridgeHandlers): BridgeRef {
  return {
    invokeMethodAsync: async (method: string, ...args: unknown[]) => {
      const fn = (handlers as Record<string, ((...a: unknown[]) => void) | undefined>)[method];
      if (typeof fn === "function") {
        try {
          fn(...args);
        } catch (err) {
          console.warn("[bridgeAdapter] handler", method, "threw:", err);
        }
      }
      return undefined;
    },
  };
}

export interface ViewerJsModule {
  createViewer(config: ViewerConfig, dotNetRef: BridgeRef): Promise<unknown>;
  destroyViewer(viewerId: string): Promise<unknown>;
  /** Legacy GameObject path: SendMessage(target, method, message). */
  sendMessage(viewerId: string, target: string, method: string, message: string): Promise<unknown>;
  /** Newer envelope-based bridge — messages routed through the WebBridge MonoBehaviour. */
  sendToUnity?(viewerId: string, method: string, json: string): Promise<unknown>;
  /** Request-response variant of sendToUnity. Unity replies via a callback id
   *  embedded in the envelope; the JS bridge resolves the returned promise
   *  with whatever JSON string Unity sent back. Used for Get* verbs like
   *  GetSceneDModel where the React side needs the result. */
  requestUnity?(viewerId: string, method: string, payload: unknown, timeoutMs?: number): Promise<string>;
  registerWebBridgeViewer?(viewerId: string, dotNetRef: BridgeRef, methodName: string): void;
  unregisterWebBridgeViewer?(viewerId: string): void;
  /**
   * Wires up the DragDropManager. Once called, any DOM element carrying
   * [data-product-id] within `containerId`'s ancestor scope (the JS module
   * attaches global pointer listeners) participates in the drag flow that
   * sends HandleDragStart/Drag/DragEnd messages to Unity.
   */
  initializeDragDrop?(viewerId: string, containerId: string, canvasId: string): Promise<unknown>;
  /** Wires up the HotkeyManager (camera nav, undo/redo bindings, etc.). */
  initializeHotkeys?(viewerId: string): Promise<unknown>;
  /** Feedback during drag — red border on ghost when invalid drop target. */
  updateDragValidation?(viewerId: string, isValid: boolean): unknown;
  /** Unity tells JS the 3D preview spawned, so the ghost can hide. */
  setUnityPreviewReady?(viewerId: string, isReady: boolean): unknown;
  /** Resize Unity's drawing buffer + fire its C# resize listeners. */
  setCanvasSize?(viewerId: string, w: number, h: number): unknown;
}

export interface ViewerConfig {
  // camelCase to match Blazor's JSInterop serializer; configurator-viewer.js destructures
  // these names. Don't rename to PascalCase — the JS module will silently see undefined.
  viewerId: string;
  version: string;
  containerId: string;
  canvasId: string;
  /** Direct element references — preferred over IDs when the mount lives inside a
   *  Shadow DOM (the embed), where document.getElementById can't find the nodes.
   *  The JS module falls back to the ID lookup when these are omitted. */
  containerEl?: HTMLElement | null;
  canvasEl?: HTMLCanvasElement | null;
  buildUrl: string;
  enableScroll?: boolean;
  showLoadingBar?: boolean;
  configuration?: Record<string, unknown> | null;
}

/** Loads the JS module by URL. Same module the Blazor side imports. */
export async function loadViewerJs(scriptUrl: string): Promise<ViewerJsModule> {
  const mod = (await import(/* @vite-ignore */ scriptUrl)) as ViewerJsModule;
  return mod;
}
